import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { PrimaryButton, Eyebrow } from "@/components/fionova/Primitives";
import { UnsaidObservations, XraySections, topObservations } from "@/components/discovery/RevealSections";
import { EllieReturns, fallbackMessage } from "@/components/discovery/EllieReturns";
import { JourneyPreviews } from "@/components/discovery/JourneyPreviews";
import { TrialOffer } from "@/components/discovery/TrialOffer";
import { Ambient, EllieOrb } from "@/components/discovery/EllieOrb";
import { getMemory, generateReveal, generateXray, generateJourney, saveMemory, startTrialCheckout } from "@/services/unsaidService";
import { unsaidStore } from "@/lib/unsaidStore";
import { useAuth } from "@/context/AuthContext";
import { track, trackSessionOnce, useExitTracking } from "@/lib/analytics";

// Server memory first; otherwise rebuild from the locally-held discovery outcome and persist.
async function loadResults() {
  let mem = {};
  try { mem = await getMemory(); } catch { /* fall through */ }
  let outcome = mem.unsaid_handoff?.observations?.length ? mem.unsaid_handoff : unsaidStore.getOutcome();
  if (!outcome || !(outcome.transcript || []).some((m) => m.role === "user")) return null;
  let changed = !mem.unsaid_handoff?.observations?.length;
  if (!outcome.observations?.length) {
    const rev = await generateReveal(outcome.transcript);
    if (!rev.ok) return null;
    outcome = { ...outcome, observations: rev.observations, handoff: { ...(outcome.handoff || {}), ...rev.handoff } };
  }
  const discoveries = mem.discoveries || outcome.handoff?.discoveries || [];
  let xray = mem.xray || unsaidStore.getXrayOutcome()?.xray || null;
  if (!xray) {
    const r = await generateXray(outcome);
    if (!r.ok) return null;
    xray = r.xray; changed = true;
  }
  const journey = mem.journey?.v >= 3 ? mem.journey : null;
  const memory = { ...mem, xray, unsaid_handoff: outcome, discoveries };
  if (changed || !mem.xray) saveMemory(memory, false);
  return { observations: outcome.observations, transcript: outcome.transcript, xray, journey, discoveries, memory };
}

// Ellie's closing message is written after the X-Ray exists, so it can reference both reveals.
async function loadJourney(r) {
  const j = await generateJourney(r.transcript, r.observations, r.discoveries, r.xray);
  if (!j.ok || !j.journey?.message?.length) return null;
  saveMemory({ ...r.memory, journey: j.journey }, false);
  return j.journey;
}

export default function XrayReveal() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [results, setResults] = useState(null);
  const [step, setStep] = useState("unsaid"); // unsaid | xray | return
  const [returned, setReturned] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const startedRef = useRef(false);
  const xrayRef = useRef(null);
  const returnRef = useRef(null);
  const trialRef = useRef(null);
  const jumpToTrial = useRef(typeof window !== "undefined" && window.location.hash === "#trial");
  const exitRef = useRef(null);
  exitRef.current = status !== "ready" || checkingOut ? null : { stage: `${step}_reveal` };
  useExitTracking(exitRef);

  useEffect(() => {
    if (loading) return;
    if (user === null) navigate("/begin", { replace: true });
    else if (user && user.entitled) navigate("/app", { replace: true });
    else if (user && !user.has_discovery) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  const load = useCallback(async () => {
    setStatus("loading");
    const r = await loadResults();
    if (!r) { setStatus("error"); return; }
    setResults(r);
    setStatus("ready");
    // Returning to the trial offer: show the saved results already opened, then go to the offer.
    if (jumpToTrial.current) setStep("return");
    if (!r.journey && !r.xray?.crisis) {
      loadJourney(r).then((journey) => setResults((prev) => ({ ...prev, journey: journey || { fallback: true } })));
    }
    trackSessionOnce("unsaid_revealed", "unsaid_revealed", { insights_count: Math.min(r.observations.length, 2) });
  }, []);

  useEffect(() => {
    if (startedRef.current || loading || !user) return;
    startedRef.current = true;
    load();
  }, [load, loading, user]);

  const scrollTo = (ref) => setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  const revealXray = () => { setStep("xray"); trackSessionOnce("xray_revealed", "emotional_xray_revealed"); scrollTo(xrayRef); };
  const ellieReturn = () => { setStep("return"); scrollTo(returnRef); };
  const onReturned = useCallback(() => {
    setReturned(true);
    if (jumpToTrial.current) { jumpToTrial.current = false; setTimeout(() => trialRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150); }
    trackSessionOnce("ellie_return_viewed", "ellie_return_viewed");
  }, []);

  const startTrial = async () => {
    if (checkingOut) return;
    setCheckingOut(true);
    track("trial_cta_clicked", { plan: "founders_monthly" });
    track("stripe_checkout_started", { plan: "founders_monthly" });
    try {
      const { checkout_url } = await startTrialCheckout(7);
      if (checkout_url) window.location.href = checkout_url;
      else setCheckingOut(false);
    } catch { setCheckingOut(false); }
  };

  if (status !== "ready") {
    return (
      <OnboardingLayout immersive testId="page-xray-reveal" contentClassName="items-center justify-center py-10 text-center">
        <EllieOrb size={64} />
        {status === "loading" ? (
          <p className="eyebrow mt-6 text-[11px] text-teal-700" data-testid="xray-reveal-loading">Bringing together what I noticed…</p>
        ) : (
          <>
            <h1 className="mt-6 text-2xl font-bold tracking-tight text-slate-900">Let me take another moment.</h1>
            <p className="mt-3 max-w-sm text-base leading-relaxed text-slate-600">I wasn't quite able to gather my thoughts. Give me a moment to try again.</p>
            <div className="mt-8 w-full max-w-[280px]"><PrimaryButton className="fio-cta" data-testid="xray-reveal-retry" onClick={load}>Try again</PrimaryButton></div>
          </>
        )}
      </OnboardingLayout>
    );
  }

  const insights = topObservations(results.observations);
  const found = results.discoveries.map((d) => d.area);
  const crisis = !!(results.xray?.crisis || results.journey?.crisis);
  const paragraphs = results.journey?.message?.length ? results.journey.message
    : results.journey?.fallback ? fallbackMessage(results.discoveries) : null;

  return (
    <div data-testid="page-xray-reveal" className="fio-immersive min-h-screen w-full">
      <Ambient />
      <div className="mx-auto w-full max-w-[580px] px-5 py-12 sm:px-8">
        <section data-testid="dual-unsaid-section">
          <div className="text-center">
            <Eyebrow className="text-violet-700">The Unsaid</Eyebrow>
            <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="mt-3 text-4xl font-extrabold leading-[1.1] tracking-tight text-slate-900">
              What I'm <span className="fio-gradient-text">beginning to notice</span>
            </motion.h1>
            <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-slate-500">
              This isn't a diagnosis, and I'm not claiming to know what you're thinking. These are patterns I noticed in what you shared — you decide what fits.
            </p>
          </div>
          <UnsaidObservations observations={insights} />
          <div className="fio-glass mt-8 flex gap-4 rounded-[28px] p-5" data-testid="unsaid-ellie-note">
            <EllieOrb size={36} testId="unsaid-ellie-note-avatar" />
            <div className="space-y-2 text-[15px] leading-relaxed text-slate-700">
              <p>These are {insights.length === 1 ? "one thing" : "two things"} I'm beginning to notice from what you've shared with me.</p>
              <p>But remember, we've only been talking for a few minutes. There's more we can explore together as I get to know you better.</p>
              <p className="font-medium text-slate-900">What we've discovered today is a starting point, not the end of your story.</p>
            </div>
          </div>
        </section>

        {step === "unsaid" ? (
          <div className="mt-10 text-center">
            <PrimaryButton className="fio-cta" data-testid="dual-show-xray" onClick={revealXray}>Show me my Emotional X-Ray <ArrowDown className="h-4 w-4" /></PrimaryButton>
          </div>
        ) : (
          <section ref={xrayRef} data-testid="dual-xray-section" className="mt-20 scroll-mt-8">
            <div className="text-center">
              <Eyebrow className="text-teal-700">Your Emotional X-Ray</Eyebrow>
              <h2 className="mt-3 text-[30px] font-extrabold leading-tight tracking-tight text-slate-900">
                What seems to be <span className="fio-gradient-text">happening right now</span>
              </h2>
            </div>
            <XraySections xray={results.xray} />
          </section>
        )}

        {step === "xray" && !crisis && (
          <div className="mt-10 text-center">
            <PrimaryButton className="fio-cta" data-testid="dual-continue" onClick={ellieReturn}>Continue</PrimaryButton>
          </div>
        )}
        {step !== "unsaid" && crisis && (
          <p className="mt-10 text-center text-sm text-slate-600" data-testid="crisis-saved-note">Your results are saved in your free account whenever you'd like to come back to them.</p>
        )}

        {step === "return" && !crisis && (
          <div ref={returnRef} className="scroll-mt-8">
            <EllieReturns paragraphs={paragraphs} found={found} discoveries={results.discoveries} order={results.journey?.order || []} onShown={onReturned} />
            {returned && (
              <>
                <div ref={trialRef} className="scroll-mt-6" data-testid="trial-offer-anchor"><TrialOffer onStart={startTrial} checkingOut={checkingOut} /></div>
                <JourneyPreviews previews={results.journey?.previews || {}} discoveries={results.discoveries}
                  onViewed={() => trackSessionOnce("premium_preview_viewed", "premium_preview_viewed", { preview_count: found.length })} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
